import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "../lib/api";

/**
 * Global inventory search bar (v6.3).
 *
 * Always searches ALL inventory (new + used) regardless of the page the
 * customer is on. Behavior:
 *   - Exact VIN / stock-number match  → redirect directly to the VDP
 *   - Anything else                   → /inventory?search=<query>
 *
 * Used in:
 *   - Header (desktop)
 *   - Header (mobile slide-out)
 *   - InventoryList (existing smart-search is kept for in-page filtering)
 */
export default function GlobalSearchBar({
  variant = "header",
  placeholder = "Search by model, stock #, VIN, new or used…",
  onNavigate,
}) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const loc = useLocation();

  // Close suggestions on outside click
  useEffect(() => {
    function onClick(e) {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", onClick);
      return () => document.removeEventListener("mousedown", onClick);
    }
  }, [open]);

  // Reset on route change
  useEffect(() => {
    setOpen(false);
    setValue("");
    setSuggestions([]);
  }, [loc.pathname]);

  const submit = useCallback(
    async (rawQuery) => {
      const q = (rawQuery ?? value).trim();
      if (!q) return;
      setBusy(true);
      try {
        const { data } = await api.get("/inventory/search", { params: { q, limit: 12 } });
        if (data?.exact_match?.slug) {
          const slug = data.exact_match.slug;
          const status = data.exact_match.status;
          const path =
            status === "used"
              ? `/used-vehicles/${slug}`
              : `/new-vehicles/${slug}`;
          navigate(path);
        } else {
          navigate(`/inventory?search=${encodeURIComponent(q)}`);
        }
        setOpen(false);
        setValue("");
        setSuggestions([]);
        onNavigate?.();
      } finally {
        setBusy(false);
      }
    },
    [value, navigate, onNavigate],
  );

  // Debounced suggestion fetch
  useEffect(() => {
    const q = value.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/inventory/search", { params: { q, limit: 6 } });
        setSuggestions(data?.results || []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [value]);

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const isHeader = variant === "header";
  const isMobile = variant === "mobile";

  return (
    <div
      ref={containerRef}
      className={`relative ${isHeader ? "w-full max-w-md" : "w-full"}`}
      data-testid={`global-search-${variant}`}
    >
      <div
        className={`flex items-center gap-2 rounded-full border-2 ${
          isHeader
            ? "border-[var(--ssc-line)] bg-white px-3 py-1.5"
            : "border-[var(--ssc-navy)] bg-white px-3 py-2"
        } focus-within:border-[var(--ssc-blue)]`}
      >
        <Search size={isMobile ? 18 : 16} className="text-[var(--ssc-ink-2)] shrink-0" />
        <input
          type="search"
          inputMode="search"
          autoComplete="off"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label="Search inventory"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-[var(--ssc-ink-2)]/70"
          data-testid={`global-search-input-${variant}`}
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              setValue("");
              setSuggestions([]);
              setOpen(false);
            }}
            aria-label="Clear search"
            className="shrink-0 text-[var(--ssc-ink-2)] hover:text-[var(--ssc-navy)]"
            data-testid={`global-search-clear-${variant}`}
          >
            <X size={14} />
          </button>
        )}
        <button
          type="button"
          onClick={() => submit()}
          disabled={busy || !value.trim()}
          className="shrink-0 ssc-btn-pill ssc-btn-pill-blue !py-1 !px-3 text-xs disabled:opacity-40"
          data-testid={`global-search-submit-${variant}`}
        >
          Search
        </button>
      </div>

      {open && suggestions.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-50 bg-white rounded-xl border border-[var(--ssc-line)] shadow-2xl py-2 max-h-[60vh] overflow-y-auto"
          data-testid={`global-search-suggestions-${variant}`}
        >
          {suggestions.map((v) => {
            const path =
              v.status === "used"
                ? `/used-vehicles/${v.slug}`
                : `/new-vehicles/${v.slug}`;
            return (
              <button
                key={v.vin || v.stock_number}
                type="button"
                onClick={() => {
                  navigate(path);
                  setOpen(false);
                  setValue("");
                  onNavigate?.();
                }}
                className="w-full text-left px-3 py-2 hover:bg-[var(--ssc-paper)] flex items-center justify-between gap-3"
                data-testid={`global-search-suggestion-${v.stock_number || v.vin}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    className={`text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0 ${
                      v.status === "new"
                        ? "bg-blue-50 text-blue-700"
                        : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {v.status}
                  </span>
                  <span className="truncate text-sm font-semibold text-[var(--ssc-navy)]">
                    {v.year} {v.make} {v.model} {v.trim}
                  </span>
                </span>
                <span className="text-xs text-[var(--ssc-ink-2)] tabular-nums shrink-0">
                  #{v.stock_number}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => submit()}
            className="w-full text-left px-3 py-2 text-xs font-bold text-[var(--ssc-blue)] hover:bg-[var(--ssc-paper)] border-t border-[var(--ssc-line)]"
            data-testid={`global-search-see-all-${variant}`}
          >
            See all matches for &quot;{value}&quot; →
          </button>
        </div>
      )}
    </div>
  );
}
